import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  Package, Plus, Camera, Sparkles, Trash2, QrCode, 
  Image as ImageIcon, Shield, AlertTriangle, Edit, Save, FileText,
  Sofa, Bed, Lamp, Tv, Utensils, BookOpen, Shirt, Box, Search, X
} from 'lucide-react';
import api from '../utils/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import QRCodePrinter from './QRCodePrinter';

// Category icons mapping
const CATEGORY_ICONS = {
  'Living Room': Sofa,
  'Bedroom': Bed,
  'Kitchen': Utensils,
  'Bathroom': Box,
  'Home Office': BookOpen,
  'Electronics': Tv,
  'Clothing': Shirt,
  'Lighting': Lamp,
  'Fragile': AlertTriangle,
  'Other': Box,
  'Boxes': Box,
};

/**
 * Unified Inventory Manager Component
 * Used across: Company Admin Panel, Customer Mobile App, Porter Mobile App
 * 
 * Props:
 * - jobId: string - The job ID to manage inventory for
 * - mode: 'admin' | 'customer' | 'porter' - Determines available features
 * - readOnly: boolean - If true, disables editing
 * - showVolume: boolean - Show volume calculations
 * - onSave: function - Callback after saving
 */
const InventoryManager = ({ 
  jobId, 
  mode = 'admin', 
  readOnly = false,
  showVolume = true,
  onSave,
  onSwitchToEstimates
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showQuickAddDialog, setShowQuickAddDialog] = useState(false);
  
  // Current item being edited
  const [currentItem, setCurrentItem] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Quick Add item state (when clicking + on template)
  const [quickAddItem, setQuickAddItem] = useState(null);
  const [quickAddQR, setQuickAddQR] = useState('');
  const [quickAddPhoto, setQuickAddPhoto] = useState(null);
  const [quickAddNotes, setQuickAddNotes] = useState('');
  const quickAddPhotoRef = useRef(null);
  
  // Custom item state (for Add Item dialog)
  const [customItemName, setCustomItemName] = useState('');
  const [customItemCategory, setCustomItemCategory] = useState('');
  const [customItemNotes, setCustomItemNotes] = useState('');
  const [customItemFragile, setCustomItemFragile] = useState(false);
  const [customItemPacking, setCustomItemPacking] = useState(false);
  const [customItemPhoto, setCustomItemPhoto] = useState(null);
  const [customItemQR, setCustomItemQR] = useState(null);
  
  // Refs
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const qrInputRef = useRef(null);
  const customCameraRef = useRef(null);

  useEffect(() => {
    if (jobId) {
      fetchData();
    }
  }, [jobId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch existing enhanced inventory (with QR, photos, packing features)
      const invResponse = await api.get(`/jobs/${jobId}/inventory-enhanced`);
      // Enhanced endpoint returns {items: [...], summary: {...}}
      const inventoryData = invResponse.data?.items || invResponse.data || [];
      setItems(Array.isArray(inventoryData) ? inventoryData : []);
      
      // Fetch templates
      const templatesResponse = await api.get('/inventory/templates?item_type=domestic');
      setTemplates(templatesResponse.data.items || []);
      setCategories(templatesResponse.data.categories?.domestic || []);
      
    } catch (error) {
      console.error('Failed to load inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Open quick add dialog when clicking + on a template item
  const openQuickAdd = (template) => {
    setQuickAddItem({
      name: template.name,
      category: template.category,
      volume: template.volume || 0.5,
      dimensions: template.dimensions || null,
      is_fragile: template.is_fragile || false
    });
    setQuickAddQR('');
    setQuickAddPhoto(null);
    setQuickAddNotes('');
    setShowQuickAddDialog(true);
  };
  
  // Save item from quick add dialog
  const saveQuickAddItem = () => {
    if (!quickAddItem) return;
    
    const existingIndex = items.findIndex(
      i => i.item_name === quickAddItem.name && i.category === quickAddItem.category && !quickAddQR && !quickAddPhoto && !quickAddNotes
    );
    
    // If same item exists without any customization, just increase quantity
    if (existingIndex >= 0 && !quickAddQR && !quickAddPhoto && !quickAddNotes) {
      const updated = [...items];
      updated[existingIndex].quantity += 1;
      setItems(updated);
      toast.success(`${quickAddItem.name} quantity increased`);
    } else {
      const newItem = {
        id: `temp-${Date.now()}`,
        job_id: jobId,
        category: quickAddItem.category,
        name: quickAddItem.name,
        item_name: quickAddItem.name,
        quantity: 1,
        is_fragile: quickAddItem.is_fragile,
        needs_packing: false,
        requires_packing: false,
        photo_url: quickAddPhoto,
        photo_urls: quickAddPhoto ? [quickAddPhoto] : [],
        qr_code: quickAddQR || null,
        notes: quickAddNotes,
        volume: quickAddItem.volume,
        dimensions: quickAddItem.dimensions,
        isNew: true
      };
      setItems([...items, newItem]);
      toast.success(`${quickAddItem.name} added${quickAddQR ? ' with QR' : ''}${quickAddPhoto ? ' with photo' : ''}`);
    }
    
    setShowQuickAddDialog(false);
    setQuickAddItem(null);
  };
  
  // Legacy function for direct add (keeping for backwards compatibility)
  const addItemFromTemplate = (template) => {
    const existingIndex = items.findIndex(
      i => i.item_name === template.name && i.category === template.category
    );
    
    if (existingIndex >= 0) {
      const updated = [...items];
      updated[existingIndex].quantity += 1;
      setItems(updated);
      toast.success(`${template.name} quantity increased`);
    } else {
      const newItem = {
        id: `temp-${Date.now()}`,
        job_id: jobId,
        category: template.category,
        name: template.name,  // Backend expects 'name' not 'item_name'
        item_name: template.name,  // Keep for display compatibility
        quantity: 1,
        is_fragile: template.is_fragile || false,
        needs_packing: false,
        requires_packing: false,  // Backend field name
        photo_url: null,
        photo_urls: [],  // Backend field name
        qr_code: null,
        notes: '',
        volume: template.volume || null,
        dimensions: template.dimensions || null,
        isNew: true
      };
      setItems([...items, newItem]);
      toast.success(`${template.name} added`);
    }
  };

  const updateItem = (index, updates) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...updates, modified: true };
    setItems(updated);
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(index);
      return;
    }
    updateItem(index, { quantity: newQuantity });
  };

  const removeItem = (index) => {
    const updated = [...items];
    const removed = updated.splice(index, 1)[0];
    setItems(updated);
    
    // If it's an existing item (has a real ID), delete from enhanced inventory
    if (removed.id && !removed.id.startsWith('temp-')) {
      api.delete(`/inventory-enhanced/${removed.id}`).catch(console.error);
    }
    
    toast.info('Item removed');
  };

  const openItemEditor = (item, index) => {
    setCurrentItem({ ...item, index });
    setShowItemDialog(true);
  };

  const saveItemDetails = () => {
    if (currentItem && currentItem.index !== undefined) {
      const updated = [...items];
      const { index, ...itemData } = currentItem;
      updated[index] = { ...updated[index], ...itemData, modified: true };
      setItems(updated);
      setShowItemDialog(false);
      setCurrentItem(null);
      toast.success('Item updated');
    }
  };

  const handlePhotoCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file || currentItem === null) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      
      // Update the item with photo
      if (currentItem.index !== undefined) {
        updateItem(currentItem.index, { photo_url: reader.result });
        toast.success('Photo added');
      }
    };
    reader.readAsDataURL(file);
    
    setShowPhotoDialog(false);
  };

  const handleQRScan = async (event) => {
    const file = event.target.files?.[0];
    if (!file || currentItem === null) return;
    
    // In production, you'd decode the QR code from the image
    // For now, generate a unique code
    const qrCode = `QR-${Date.now().toString(36).toUpperCase()}`;
    
    if (currentItem.index !== undefined) {
      updateItem(currentItem.index, { qr_code: qrCode });
      toast.success(`QR code linked: ${qrCode}`);
    }
    
    setShowQRDialog(false);
  };

  // Handle custom item photo capture
  const handleCustomPhotoCapture = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomItemPhoto(reader.result);
      toast.success('Photo added to custom item');
    };
    reader.readAsDataURL(file);
  };

  // Add custom item with all details
  const addCustomItem = () => {
    if (!customItemName.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    
    const newItem = {
      id: `temp-${Date.now()}`,
      item_name: customItemName.trim(),
      name: customItemName.trim(),
      category: customItemCategory || 'General',
      quantity: 1,
      volume: 0.5, // Default volume
      is_fragile: customItemFragile,
      needs_packing: customItemPacking,
      requires_packing: customItemPacking,
      notes: customItemNotes,
      photo_url: customItemPhoto,
      qr_code: customItemQR,
      isNew: true
    };
    
    setItems([...items, newItem]);
    
    // Clear the form
    setCustomItemName('');
    setCustomItemCategory('');
    setCustomItemNotes('');
    setCustomItemFragile(false);
    setCustomItemPacking(false);
    setCustomItemPhoto(null);
    setCustomItemQR(null);
    
    toast.success(`"${newItem.item_name}" added to inventory`);
  };

  // Reset custom item form when dialog closes
  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    setCustomItemName('');
    setCustomItemCategory('');
    setCustomItemNotes('');
    setCustomItemFragile(false);
    setCustomItemPacking(false);
    setCustomItemPhoto(null);
    setCustomItemQR(null);
  };

  const togglePacking = (index) => {
    const item = items[index];
    updateItem(index, { needs_packing: !item.needs_packing });
    toast.success(item.needs_packing ? 'Packing service removed' : 'Packing service requested');
  };

  const saveInventory = async () => {
    setSaving(true);
    const itemsToSave = Array.isArray(items) ? items : [];
    
    try {
      // Save each new or modified item to enhanced inventory
      for (const item of itemsToSave) {
        if (item.isNew) {
          const { isNew, modified, index, ...itemData } = item;
          await api.post(`/jobs/${jobId}/inventory-enhanced`, itemData);
        } else if (item.modified) {
          const { isNew, modified, index, ...itemData } = item;
          await api.put(`/inventory-enhanced/${item.id}`, itemData);
        }
      }
      
      toast.success('Inventory saved successfully!');
      if (onSave) onSave(itemsToSave);
      fetchData(); // Refresh to get server IDs
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save inventory');
    } finally {
      setSaving(false);
    }
  };

  // Calculate total volume (ensure items is always an array)
  const safeItems = Array.isArray(items) ? items : [];
  const totalVolume = safeItems.reduce((sum, item) => {
    const vol = parseFloat(item.volume) || 0;
    return sum + (vol * (item.quantity || 1));
  }, 0);
  
  // Add 10% buffer
  const totalVolumeWithBuffer = totalVolume * 1.1;

  const safeTemplates = Array.isArray(templates) ? templates : [];
  const filteredTemplates = safeTemplates.filter(item => {
    const matchesSearch = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Package className="animate-pulse text-primary mr-2" size={24} />
        <span className="text-gray-500">Loading inventory...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="inventory-manager">
      {/* Header with actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Package size={20} />
            {t('inventory.title', 'Inventory')} ({safeItems.length} {t('common.items', 'items')})
          </h3>
          {showVolume && totalVolume > 0 && (
            <p className="text-sm text-gray-500">
              {t('inventory.total_volume', 'Total Volume')}: {totalVolume.toFixed(2)} m³ ({t('inventory.with_buffer', 'with buffer')}: {totalVolumeWithBuffer.toFixed(2)} m³)
            </p>
          )}
        </div>
        
        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowAddDialog(true)} data-testid="add-item-btn">
              <Plus size={16} className="mr-1" />
              {t('inventory.add_item', 'Add Item')}
            </Button>
            {(mode === 'admin' || mode === 'customer') && (
              <Button variant="outline" onClick={() => navigate('/ai-scanner')} data-testid="ai-scan-btn">
                <Sparkles size={16} className="mr-1" />
                {t('inventory.ai_scan', 'AI Scan')}
              </Button>
            )}
            <Button onClick={saveInventory} disabled={saving} data-testid="save-inventory-btn">
              <Save size={16} className="mr-1" />
              {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
            {mode === 'admin' && safeItems.length > 0 && (
              <Button 
                variant="default" 
                className="bg-green-600 hover:bg-green-700"
                disabled={saving}
                onClick={async () => {
                  if (!jobId) {
                    toast.error(t('inventory.select_job_first', 'Please select a job first'));
                    return;
                  }
                  
                  // Check if there are unsaved items
                  const hasUnsavedItems = safeItems.some(item => item.isNew || item.modified);
                  
                  if (hasUnsavedItems) {
                    // Auto-save inventory first to enhanced inventory
                    setSaving(true);
                    try {
                      for (const item of safeItems) {
                        if (item.isNew) {
                          const { isNew, modified, index, ...itemData } = item;
                          await api.post(`/jobs/${jobId}/inventory-enhanced`, itemData);
                        } else if (item.modified) {
                          const { isNew, modified, index, ...itemData } = item;
                          await api.put(`/inventory-enhanced/${item.id}`, itemData);
                        }
                      }
                      toast.success('Inventory saved!');
                      // Refresh to get server IDs
                      await fetchData();
                    } catch (error) {
                      console.error('Save error:', error);
                      toast.error('Failed to save inventory. Please try again.');
                      setSaving(false);
                      return;
                    }
                    setSaving(false);
                  }
                  
                  // Now switch to estimates tab
                  if (onSwitchToEstimates) {
                    onSwitchToEstimates();
                  } else {
                    navigate(`/control-panel/jobs/${jobId}?tab=estimates`);
                  }
                }} 
                data-testid="create-estimate-btn"
              >
                <FileText size={16} className="mr-1" />
                {t('inventory.create_estimate', 'Create Estimate')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Category Pills - Horizontally Scrollable */}
      <div className="w-full overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="flex gap-2 pb-2 min-w-max">
          <Badge
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            className="cursor-pointer shrink-0"
            onClick={() => setSelectedCategory('all')}
          >
            All
          </Badge>
          {categories.map((cat) => {
            const IconComponent = CATEGORY_ICONS[cat] || Box;
            return (
              <Badge
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                className="cursor-pointer shrink-0 flex items-center gap-1"
                onClick={() => setSelectedCategory(cat)}
              >
                <IconComponent size={12} />
                {cat}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Inventory List */}
      <Card>
        <CardContent className="pt-4">
          {safeItems.length > 0 ? (
            <div className="space-y-3">
              {safeItems.filter(item => 
                selectedCategory === 'all' || item.category === selectedCategory
              ).map((item, idx) => {
                const actualIndex = safeItems.findIndex(i => i.id === item.id);
                return (
                  <div 
                    key={item.id} 
                    className={`p-3 border rounded-lg transition-all ${
                      item.is_fragile ? 'bg-orange-50 border-orange-200' : ''
                    } ${item.needs_packing ? 'ring-2 ring-blue-300' : ''}`}
                    data-testid={`inventory-item-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{item.item_name || item.name}</p>
                          <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                          {item.is_fragile && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle size={10} className="mr-1" />
                              Fragile
                            </Badge>
                          )}
                          {item.needs_packing && (
                            <Badge className="text-xs bg-blue-500">
                              <Shield size={10} className="mr-1" />
                              Packing
                            </Badge>
                          )}
                        </div>
                        
                        {/* Indicators */}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {item.qr_code && (
                            <span className="flex items-center gap-1">
                              <QrCode size={12} />
                              {item.qr_code}
                            </span>
                          )}
                          {item.photo_url && (
                            <span className="flex items-center gap-1">
                              <ImageIcon size={12} />
                              Photo
                            </span>
                          )}
                          {item.volume && (
                            <span>{item.volume} m³</span>
                          )}
                        </div>
                        
                        {item.notes && (
                          <p className="text-xs text-gray-400 mt-1 truncate">{item.notes}</p>
                        )}
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        {!readOnly && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => updateQuantity(actualIndex, item.quantity - 1)}
                            >
                              -
                            </Button>
                          </>
                        )}
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        {!readOnly && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => updateQuantity(actualIndex, item.quantity + 1)}
                          >
                            +
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Item Actions */}
                    {!readOnly && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCurrentItem({ ...item, index: actualIndex });
                            setShowPhotoDialog(true);
                          }}
                        >
                          <Camera size={14} className="mr-1" />
                          Photo
                        </Button>
                        
                        {(mode === 'admin' || mode === 'porter') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCurrentItem({ ...item, index: actualIndex });
                              setShowQRDialog(true);
                            }}
                          >
                            <QrCode size={14} className="mr-1" />
                            QR
                          </Button>
                        )}
                        
                        {item.is_fragile && (
                          <Button
                            variant={item.needs_packing ? "default" : "outline"}
                            size="sm"
                            onClick={() => togglePacking(actualIndex)}
                          >
                            <Shield size={14} className="mr-1" />
                            {item.needs_packing ? 'Packing ✓' : 'Request Packing'}
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openItemEditor(item, actualIndex)}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => removeItem(actualIndex)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium">No items in inventory</p>
              <p className="text-sm mt-1">Click &quot;Add Item&quot; to start building the inventory</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => !open && handleCloseAddDialog()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Items</DialogTitle>
            <DialogDescription>Select from templates or add custom items with photos and QR codes</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <Input
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Category pills with proper horizontal scroll - using native CSS for better browser support */}
            <div 
              className="w-full overflow-x-auto pb-3"
              style={{ 
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 #f1f5f9'
              }}
            >
              <div className="flex gap-2 min-w-max px-1">
                <Badge
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  className="cursor-pointer shrink-0 whitespace-nowrap"
                  onClick={() => setSelectedCategory('all')}
                >
                  All
                </Badge>
                {categories.map((cat) => (
                  <Badge
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    className="cursor-pointer shrink-0 whitespace-nowrap"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            </div>
            
            <ScrollArea className="h-[350px]">
              <div className="space-y-2 pr-4">
                {filteredTemplates.slice(0, 50).map((item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="p-3 border rounded-lg flex justify-between items-center hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => openQuickAdd(item)}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{item.category}</span>
                        {item.dimensions && <span>• {item.dimensions}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.is_fragile && (
                        <Badge variant="destructive" className="text-xs">Fragile</Badge>
                      )}
                      <Plus size={18} className="text-primary" />
                    </div>
                  </div>
                ))}
                {filteredTemplates.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No items found</p>
                )}
              </div>
            </ScrollArea>
            
            {/* Add Custom Item Section */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium mb-3">Or add a custom item:</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Item Name *</Label>
                    <Input
                      value={customItemName}
                      onChange={(e) => setCustomItemName(e.target.value)}
                      placeholder="e.g., Antique Vase"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Category</Label>
                    <Select
                      value={customItemCategory}
                      onValueChange={setCustomItemCategory}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    value={customItemNotes}
                    onChange={(e) => setCustomItemNotes(e.target.value)}
                    placeholder="Special handling instructions, condition notes..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={customItemFragile}
                      onChange={(e) => setCustomItemFragile(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-xs">Fragile</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={customItemPacking}
                      onChange={(e) => setCustomItemPacking(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-xs">Needs Packing</span>
                  </label>
                </div>
                
                {/* Action Buttons: QR Scan, Camera, Add */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Trigger QR scanner
                      setShowQRScanner(true);
                    }}
                    className="flex-1"
                  >
                    <QrCode size={16} className="mr-2" />
                    Scan QR
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (customCameraRef.current) {
                        customCameraRef.current.click();
                      }
                    }}
                    className="flex-1"
                  >
                    <Camera size={16} className="mr-2" />
                    Take Photo
                  </Button>
                  <Button
                    size="sm"
                    onClick={addCustomItem}
                    disabled={!customItemName.trim()}
                    className="flex-1"
                  >
                    <Plus size={16} className="mr-2" />
                    Add Item
                  </Button>
                </div>
                
                {/* Custom item photo preview */}
                {customItemPhoto && (
                  <div className="relative">
                    <img src={customItemPhoto} alt="Custom item" className="w-full h-24 object-cover rounded-lg" />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => setCustomItemPhoto(null)}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                )}
                
                {/* Scanned QR code indicator */}
                {customItemQR && (
                  <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <QrCode size={16} className="text-green-600" />
                      <span className="text-sm text-green-700 font-medium">QR: {customItemQR}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-green-600 hover:text-green-800"
                      onClick={() => setCustomItemQR(null)}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                )}
                
                {/* Hidden file input for camera */}
                <input
                  ref={customCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleCustomPhotoCapture}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAddDialog}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
          </DialogHeader>
          
          {currentItem && (
            <div className="space-y-4">
              <div>
                <Label>Item Name</Label>
                <Input
                  value={currentItem.item_name}
                  onChange={(e) => setCurrentItem({ ...currentItem, item_name: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <Label>Volume (m³)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={currentItem.volume || ''}
                    onChange={(e) => setCurrentItem({ ...currentItem, volume: parseFloat(e.target.value) || null })}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentItem.is_fragile}
                    onChange={(e) => setCurrentItem({ ...currentItem, is_fragile: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Fragile</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentItem.needs_packing}
                    onChange={(e) => setCurrentItem({ ...currentItem, needs_packing: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Needs Packing</span>
                </label>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={currentItem.notes || ''}
                  onChange={(e) => setCurrentItem({ ...currentItem, notes: e.target.value })}
                  placeholder="Add any special notes about this item..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={saveItemDetails}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="text-primary" />
              Add Photo
            </DialogTitle>
            <DialogDescription>
              Take a photo of {currentItem?.item_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {currentItem?.photo_url && (
              <img src={currentItem.photo_url} alt="Item" className="w-full rounded-lg max-h-48 object-cover" />
            )}
            
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoCapture}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoCapture}
            />
            
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => cameraInputRef.current?.click()}>
                <Camera size={18} className="mr-2" />
                Camera
              </Button>
              <Button variant="outline" onClick={() => galleryInputRef.current?.click()}>
                <ImageIcon size={18} className="mr-2" />
                Gallery
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="text-primary" />
              {currentItem?.qr_code ? 'QR Code Label' : 'Scan QR Code'}
            </DialogTitle>
            <DialogDescription>
              {currentItem?.qr_code 
                ? `QR code for ${currentItem?.item_name}` 
                : `Link a QR code to ${currentItem?.item_name}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {currentItem?.qr_code && (
              <div className="bg-gray-100 rounded-lg p-4 text-center">
                <QrCode size={48} className="mx-auto mb-2 text-gray-600" />
                <p className="font-mono font-bold text-lg">{currentItem.qr_code}</p>
                <p className="text-sm text-gray-500 mt-1">{currentItem.item_name}</p>
              </div>
            )}
            
            <input
              ref={qrInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleQRScan}
            />
            
            {!currentItem?.qr_code && (
              <Button className="w-full" onClick={() => qrInputRef.current?.click()}>
                <QrCode size={18} className="mr-2" />
                Scan QR Code
              </Button>
            )}
            
            {/* Print QR Code (Porter and Admin modes) */}
            {currentItem?.qr_code && (mode === 'porter' || mode === 'admin') && (
              <div className="border-t pt-4">
                <QRCodePrinter
                  qrData={currentItem.qr_code}
                  itemName={currentItem.item_name}
                  onPrintSuccess={() => toast.success('Label printed!')}
                  onPrintError={(err) => console.error('Print error:', err)}
                />
              </div>
            )}
            
            {mode === 'porter' && !currentItem?.qr_code && (
              <p className="text-xs text-gray-500 text-center">
                Point camera at the QR code on the item label
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog for Custom Items */}
      <Dialog open={showQRScanner} onOpenChange={(open) => {
        if (!open) {
          // Clean up scanner when closing
          const scannerElement = document.getElementById('qr-reader');
          if (scannerElement) {
            scannerElement.innerHTML = '';
          }
        }
        setShowQRScanner(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="text-primary" />
              Scan QR Code
            </DialogTitle>
            <DialogDescription>
              Point your camera at a QR code or upload an image containing one
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* QR Scanner Container */}
            <div id="qr-reader" className="w-full rounded-lg overflow-hidden" />
            
            {/* Manual Entry Option */}
            <div className="flex gap-2">
              <Input
                placeholder="Or enter QR code manually..."
                id="manual-qr-input"
                className="flex-1"
              />
              <Button
                variant="secondary"
                onClick={() => {
                  const input = document.getElementById('manual-qr-input');
                  if (input && input.value.trim()) {
                    setCustomItemQR(input.value.trim());
                    toast.success(`QR code set: ${input.value.trim()}`);
                    setShowQRScanner(false);
                  } else {
                    toast.error('Please enter a QR code');
                  }
                }}
              >
                Set
              </Button>
            </div>
            
            {/* Start Scanner Button */}
            <Button 
              onClick={async () => {
                try {
                  const { Html5Qrcode } = await import('html5-qrcode');
                  const html5QrCode = new Html5Qrcode("qr-reader");
                  
                  await html5QrCode.start(
                    { facingMode: "environment" },
                    {
                      fps: 10,
                      qrbox: { width: 250, height: 250 }
                    },
                    (decodedText) => {
                      // QR code found
                      setCustomItemQR(decodedText);
                      toast.success(`QR code scanned: ${decodedText}`);
                      html5QrCode.stop().catch(console.error);
                      setShowQRScanner(false);
                    },
                    (errorMessage) => {
                      // Ignore scanning errors (no QR found yet)
                    }
                  );
                } catch (err) {
                  console.error('QR Scanner error:', err);
                  toast.error('Camera access denied or not available. Please enter code manually.');
                }
              }}
              className="w-full"
            >
              <Camera size={18} className="mr-2" />
              Start Camera Scanner
            </Button>
            
            {/* Upload Image Option */}
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="qr-image-upload"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      const { Html5Qrcode } = await import('html5-qrcode');
                      const html5QrCode = new Html5Qrcode("qr-reader");
                      const result = await html5QrCode.scanFile(file, true);
                      setCustomItemQR(result);
                      toast.success(`QR code found: ${result}`);
                      setShowQRScanner(false);
                    } catch (err) {
                      console.error('QR scan error:', err);
                      toast.error('No QR code found in image');
                    }
                  }
                }}
              />
              <Button 
                variant="outline"
                onClick={() => document.getElementById('qr-image-upload')?.click()}
                className="w-full"
              >
                <Package size={18} className="mr-2" />
                Upload QR Code Image
              </Button>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-gray-500">
                QR codes help track items during loading/unloading
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRScanner(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Dialog - Simple popup when clicking + on template item */}
      <Dialog open={showQuickAddDialog} onOpenChange={setShowQuickAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="text-green-600" size={20} />
              {quickAddItem?.name}
            </DialogTitle>
            <DialogDescription>
              Add to inventory with optional QR code and photo
            </DialogDescription>
          </DialogHeader>
          
          {quickAddItem && (
            <div className="space-y-4">
              {/* Item info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{quickAddItem.category}</span>
                  {quickAddItem.is_fragile && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle size={12} className="mr-1" />
                      Fragile
                    </Badge>
                  )}
                </div>
                {quickAddItem.volume && (
                  <div className="text-xs text-gray-500 mt-1">
                    Volume: {quickAddItem.volume}m³
                  </div>
                )}
              </div>
              
              {/* QR & Photo Row */}
              <div className="flex gap-2">
                <Button
                  variant={quickAddQR ? "default" : "outline"}
                  className="flex-1"
                  onClick={async () => {
                    try {
                      const { Html5Qrcode } = await import('html5-qrcode');
                      const scannerId = 'quick-add-qr-reader';
                      
                      // Create container if not exists
                      let container = document.getElementById(scannerId);
                      if (!container) {
                        container = document.createElement('div');
                        container.id = scannerId;
                        container.style.position = 'fixed';
                        container.style.top = '50%';
                        container.style.left = '50%';
                        container.style.transform = 'translate(-50%, -50%)';
                        container.style.zIndex = '9999';
                        container.style.backgroundColor = 'white';
                        container.style.padding = '20px';
                        container.style.borderRadius = '8px';
                        container.style.boxShadow = '0 10px 40px rgba(0,0,0,0.3)';
                        document.body.appendChild(container);
                      }
                      
                      const html5QrCode = new Html5Qrcode(scannerId);
                      
                      await html5QrCode.start(
                        { facingMode: "environment" },
                        { fps: 10, qrbox: { width: 200, height: 200 } },
                        (decodedText) => {
                          setQuickAddQR(decodedText);
                          toast.success(`QR scanned: ${decodedText}`);
                          html5QrCode.stop().then(() => {
                            container.remove();
                          }).catch(console.error);
                        },
                        () => {}
                      );
                      
                      // Add close button
                      const closeBtn = document.createElement('button');
                      closeBtn.innerText = '✕ Close';
                      closeBtn.style.marginTop = '10px';
                      closeBtn.style.padding = '8px 16px';
                      closeBtn.style.cursor = 'pointer';
                      closeBtn.onclick = () => {
                        html5QrCode.stop().then(() => {
                          container.remove();
                        }).catch(console.error);
                      };
                      container.appendChild(closeBtn);
                      
                    } catch (err) {
                      console.error('QR error:', err);
                      const code = prompt('Enter QR code manually:');
                      if (code) {
                        setQuickAddQR(code);
                        toast.success(`QR set: ${code}`);
                      }
                    }
                  }}
                >
                  <QrCode size={16} className="mr-1" />
                  {quickAddQR ? 'QR ✓' : 'Scan QR'}
                </Button>
                
                <Button
                  variant={quickAddPhoto ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => quickAddPhotoRef.current?.click()}
                >
                  <Camera size={16} className="mr-1" />
                  {quickAddPhoto ? 'Photo ✓' : 'Photo'}
                </Button>
                
                <input
                  ref={quickAddPhotoRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setQuickAddPhoto(reader.result);
                        toast.success('Photo captured');
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>
              
              {/* Preview row */}
              {(quickAddQR || quickAddPhoto) && (
                <div className="flex gap-2">
                  {quickAddQR && (
                    <div className="flex-1 p-2 bg-green-50 rounded text-xs text-green-700 flex items-center gap-1">
                      <QrCode size={14} />
                      {quickAddQR.length > 15 ? quickAddQR.substring(0, 15) + '...' : quickAddQR}
                      <button onClick={() => setQuickAddQR('')} className="ml-auto text-green-600 hover:text-green-800">
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {quickAddPhoto && (
                    <div className="flex-1 relative">
                      <img src={quickAddPhoto} alt="Preview" className="w-full h-16 object-cover rounded" />
                      <button 
                        onClick={() => setQuickAddPhoto(null)} 
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Notes */}
              <Textarea
                placeholder="Add notes (optional)..."
                value={quickAddNotes}
                onChange={(e) => setQuickAddNotes(e.target.value)}
                rows={2}
                className="text-sm"
              />
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowQuickAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveQuickAddItem} className="bg-green-600 hover:bg-green-700">
              <Save size={16} className="mr-1" />
              Save Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryManager;
